import { ColumnDef, type Column } from '@tanstack/react-table'
import { TenantFeatureToggle } from '@xeetr/types/enums'
import {
  BaseBookingModel,
  BookingCustomField,
  FoodBookingModel,
  ServiceBookingModel,
} from '@xeetr/types/models'
import { DATE_FORMAT_LONGYEAR, isFoodTenant, isServiceTenant } from '@xeetr/types/utils'
import { format } from 'date-fns'
import { ArrowDown01, ArrowDownAZ, ArrowUp10, ArrowUpDown, ArrowUpZA } from 'lucide-react'
import { useMemo } from 'react'

import {
  AddressColumn,
  AmountColumn,
  DateColumn,
  DateTimeColumn,
  EditColumn,
  EmployeeColumn,
  InfoColumn,
  NameColumn,
  NoteColumn,
  RoomColumn,
  ServiceColumn,
  StateColumn,
  TableColumn,
  TimeColumn,
} from '../../components/dashboard/columns'
import useFeatureToggle from '../common/useFeatureToggle'
import { useWindowSize } from '../common/useWindowSize'
import { useTenant } from '../useTenant'
import { useUserSettings } from '../useUserSettings'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import clsx from 'clsx'

const createSortableHeader = (label: string, isNumericColumn = false) => {
  return ({ column }: { column: Column<any, unknown> }) => {
    const AscIcon = isNumericColumn ? ArrowDown01 : ArrowDownAZ
    const DescIcon = isNumericColumn ? ArrowUp10 : ArrowUpZA

    return (
      <Button
        variant="ghost"
        onClick={() => {
          const sortDir = column.getIsSorted()
          if (sortDir === 'asc' || !sortDir) {
            column.toggleSorting(sortDir === 'asc')
          } else if (sortDir === 'desc') {
            column.clearSorting()
          }
        }}
        className="-ml-4"
      >
        {label}
        {column.getIsSorted() === 'asc' && <AscIcon className="text-primary ml-2 h-4 w-4" />}
        {column.getIsSorted() === 'desc' && <DescIcon className="text-primary ml-2 h-4 w-4" />}
        {!column.getIsSorted() && <ArrowUpDown className="ml-2 h-4 w-4" />}
      </Button>
    )
  }
}

/**
 * Generates column definitions for booking custom fields
 */
const generateCustomFieldColumns = (
  customFields?: BookingCustomField[]
): ColumnDef<BaseBookingModel>[] => {
  if (!customFields?.length) return []

  return customFields.map((customField) => ({
    id: `customField_${customField.id}`,
    accessorFn: (row: BaseBookingModel) => {
      const customFields = row.bookingAdditions?.customFields
      return customFields?.[customField.id] ?? ''
    },
    header: createSortableHeader(customField.name),
    cell: ({ row }) => {
      const value = row.getValue(`customField_${customField.id}`)
      let displayValue: string | number | boolean = ''

      if (value === null || value === undefined) {
        displayValue = ''
      } else if (customField.type === 'date' && value) {
        displayValue = format(new Date(value as string), DATE_FORMAT_LONGYEAR)
      } else if (customField.type === 'boolean') {
        displayValue = value ? '✓' : ''
      } else if (customField.type === 'number') {
        displayValue = value as number
      } else {
        displayValue = String(value)
      }

      return (
        <div className={clsx('max-w-[20ch]', { 'text-center': customField.type === 'boolean' })}>
          {displayValue}
        </div>
      )
    },
    enableSorting: true,
    enableHiding: true,
    sortingFn: (rowA, rowB) => {
      const aValue = rowA.getValue(`customField_${customField.id}`)
      const bValue = rowB.getValue(`customField_${customField.id}`)

      if (aValue === bValue) return 0
      if (aValue == null) return 1
      if (bValue == null) return -1

      switch (customField.type) {
        case 'number':
          return (aValue as number) - (bValue as number)
        case 'boolean':
          return (aValue as boolean) === (bValue as boolean) ? 0 : (aValue as boolean) ? 1 : -1
        case 'date':
          const aDate = new Date(aValue as string).getTime()
          const bDate = new Date(bValue as string).getTime()
          return aDate - bDate
        case 'enum':
          if (customField.options) {
            const aIndex = customField.options.indexOf(aValue as string)
            const bIndex = customField.options.indexOf(bValue as string)
            if (aIndex === -1 && bIndex === -1) return 0
            if (aIndex === -1) return 1
            if (bIndex === -1) return -1
            return aIndex - bIndex
          }
          return (aValue as string).localeCompare(bValue as string)
        case 'text':
        default:
          return (aValue as string).localeCompare(bValue as string)
      }
    },
    meta: {
      name: customField.name,
      displayName: customField.description,
    },
  }))
}

export const useDashboardColumns = (): ColumnDef<BaseBookingModel>[] => {
  const { tenant } = useTenant()
  const { settings } = useUserSettings()

  const { width } = useWindowSize()
  const customerAddressFeature = useFeatureToggle(TenantFeatureToggle.CUSTOMER_ADDRESS)

  const mergeDateAndTime = useMemo(() => {
    if (!tenant?.id) return false
    return settings.dashboard?.[tenant.id]?.mergeDateAndTime ?? false
  }, [settings.dashboard, tenant?.id])

  return useMemo(() => {
    const allColumns: ColumnDef<BaseBookingModel>[] = [
      {
        id: 'state',
        accessorKey: 'state',
        header: '',
        cell: ({ row }) => <div className="w-6 md:w-16">{StateColumn(row.original)}</div>,
        enableSorting: false,
        enableHiding: false,
        meta: {
          displayName: '',
        },
      },
      {
        id: 'date',
        accessorKey: 'from',
        header: createSortableHeader('Datum'),
        cell: ({ row }) => (
          <div className="w-[10ch] text-center">
            {mergeDateAndTime ? DateTimeColumn(row.original) : DateColumn(row.original)}
          </div>
        ),
        enableSorting: true,
        enableHiding: true,
        sortingFn: (rowA, rowB) => {
          const aTime = new Date(rowA.original.from).getTime()
          const bTime = new Date(rowB.original.from).getTime()

          if (aTime !== bTime) {
            return aTime - bTime
          }

          const aTimeEnd = rowA.original.until ? new Date(rowA.original.until).getTime() : 0
          const bTimeEnd = rowB.original.until ? new Date(rowB.original.until).getTime() : 0

          return aTimeEnd - bTimeEnd
        },
        meta: {
          displayName: 'Datum',
        },
      },
      ...(!mergeDateAndTime
        ? [
            {
              id: 'time',
              accessorKey: 'from',
              header: width > 425 ? 'Uhrzeit' : 'Zeit',
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              cell: ({ row }: any) => (
                <div
                  className={cn('text-nowrap text-center', {
                    'w-[13ch]': !tenant?.useLengthOfStay,
                    'w-[9ch]': tenant?.useLengthOfStay,
                  })}
                >
                  {TimeColumn(row.original)}
                </div>
              ),
              enableSorting: false,
              enableHiding: true,
              meta: {
                displayName: width > 425 ? 'Uhrzeit' : 'Zeit',
              },
            },
          ]
        : []),
      {
        id: 'amount',
        accessorKey: 'amount',
        header: createSortableHeader('Anzahl', true),
        // @ts-expect-error -- AmountColumn expects FoodBookingModel
        cell: ({ row }) => <div className="w-[5ch] text-center">{AmountColumn(row.original)}</div>,
        enableSorting: true,
        enableHiding: true,
        sortingFn: (rowA, rowB) => {
          const aValue = rowA.getValue('amount')
          const bValue = rowB.getValue('amount')

          if (aValue === bValue) return 0
          if (aValue == null) return 1
          if (bValue == null) return -1

          return aValue < bValue ? -1 : 1
        },
        meta: {
          displayName: 'Anzahl',
        },
      },
      {
        id: 'name',
        accessorKey: 'customer.firstName',
        header: createSortableHeader('Name'),
        cell: ({ row }) => (
          <div className="max-w-[10ch] hover:break-words md:max-w-[20ch]">
            {NameColumn(row.original)}
          </div>
        ),
        enableSorting: true,
        enableHiding: true,
        sortingFn: (rowA, rowB) => {
          const aFirstName = rowA.original.customer?.firstName || ''
          const aLastName = rowA.original.customer?.lastName || ''
          const bFirstName = rowB.original.customer?.firstName || ''
          const bLastName = rowB.original.customer?.lastName || ''

          const aFullName = `${aFirstName} ${aLastName}`.trim().toLowerCase()
          const bFullName = `${bFirstName} ${bLastName}`.trim().toLowerCase()

          return aFullName.localeCompare(bFullName)
        },
        meta: {
          displayName: 'Name',
        },
      },
      {
        id: 'address',
        accessorKey: 'customer.address',
        header: 'Adresse',
        cell: ({ row }) => <div className="max-w-[25ch]">{AddressColumn(row.original)}</div>,
        enableSorting: false,
        enableHiding: true,
        meta: {
          displayName: 'Adresse',
        },
      },
      {
        id: 'info',
        accessorKey: 'bookingAdditions.notes',
        header: 'Info',
        cell: ({ row }) => <div className="max-w-[20ch]">{InfoColumn(row.original)}</div>,
        enableSorting: false,
        enableHiding: true,
        meta: {
          displayName: 'Info',
        },
      },
      {
        id: 'room',
        accessorKey: 'room',
        header: 'Raum',
        cell: ({ row }) => (
          <div className="max-w-[20ch]">{RoomColumn(row.original as FoodBookingModel)}</div>
        ),
        enableSorting: false,
        enableHiding: true,
        meta: {
          displayName: 'Raum',
        },
      },
      {
        id: 'table',
        accessorFn: (row: BaseBookingModel) => {
          const tables = (row as FoodBookingModel).tables
          return tables?.map((table) => table.name).join(', ') ?? ''
        },
        header: createSortableHeader('Tisch'),
        cell: ({ row }) => (
          <div className="max-w-[15ch]">{TableColumn(row.original as FoodBookingModel)}</div>
        ),
        enableSorting: true,
        enableHiding: true,
        sortingFn: (rowA, rowB) => {
          const aValue =
            (rowA.original as FoodBookingModel).tables?.map((table) => table.name).join(', ') ?? ''
          const bValue =
            (rowB.original as FoodBookingModel).tables?.map((table) => table.name).join(', ') ?? ''

          return aValue.localeCompare(bValue)
        },
        meta: {
          displayName: 'Tisch',
        },
      },
      {
        id: 'service',
        accessorKey: 'service',
        header: 'Dienstleistung',
        cell: ({ row }) => (
          <div className="max-w-[20ch]">{ServiceColumn(row.original as ServiceBookingModel)}</div>
        ),
        enableSorting: false,
        enableHiding: true,
        meta: {
          displayName: 'Dienstleistung',
        },
      },
      {
        id: 'employee',
        accessorKey: 'employee',
        header: 'Mitarbeiter',
        // @ts-expect-error -- EmployeeColumn expects ServiceBookingModel
        cell: ({ row }) => <div className="max-w-[20ch]">{EmployeeColumn(row.original)}</div>,
        enableSorting: false,
        enableHiding: true,
        meta: {
          displayName: 'Mitarbeiter',
        },
      },
      {
        id: 'note',
        accessorKey: 'note',
        header: 'Notiz',
        cell: ({ row }) => <div className="max-w-[20ch]">{NoteColumn(row.original)}</div>,
        enableSorting: false,
        enableHiding: true,
        meta: {
          displayName: 'Notiz',
        },
      },
      {
        id: 'created',
        accessorFn: (row: BaseBookingModel) => row.creationDetails?.created,
        header: createSortableHeader('Erstellungsdatum'),
        cell: ({ row }) => {
          const created = row.original.creationDetails?.created
          return (
            <div className="text-nowrap text-center">
              {created ? format(new Date(created), `${DATE_FORMAT_LONGYEAR}, HH:mm`) : ''}
            </div>
          )
        },
        enableSorting: true,
        enableHiding: true,
        sortingFn: (rowA, rowB) => {
          const aCreated = rowA.original.creationDetails?.created
          const bCreated = rowB.original.creationDetails?.created

          if (aCreated === bCreated) return 0
          if (!aCreated) return 1
          if (!bCreated) return -1

          return new Date(aCreated).getTime() - new Date(bCreated).getTime()
        },
        meta: {
          displayName: 'Erstellungsdatum',
        },
      },
      ...generateCustomFieldColumns(tenant?.bookingCustomFields),
      {
        id: 'edit',
        accessorKey: 'id',
        header: '',
        // @ts-expect-error -- EditColumn expects FoodBookingModel or ServiceBookingModel
        cell: ({ row }) => <div className="md:w-16">{EditColumn(row.original)}</div>,
        enableSorting: false,
        enableHiding: false,
        meta: {
          displayName: '',
        },
      },
    ]

    return allColumns.filter((col) => {
      switch (col.id) {
        case 'amount':
          return isFoodTenant(tenant)
        case 'address':
          return customerAddressFeature && width > 700
        case 'info':
          return width > 1000
        case 'room':
        case 'table':
          return isFoodTenant(tenant) && width > 700
        case 'service':
        case 'employee':
          return isServiceTenant(tenant) && width > 700
        case 'note':
          return width > 700
        default:
          return true
      }
    })
  }, [tenant, width, customerAddressFeature, mergeDateAndTime])
}
